import { useState, useEffect, useRef } from 'react';
import { CaretDown, Check, Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ProfileSwitcher — top-left selector for multi-tenant profiles.
 * Reads /api/profiles, shows a pill-button with current profile color,
 * lets the user switch via /api/profiles/switch (sets cookie). Reloads the
 * page after switch so all data refreshes for the new profile.
 */
export default function ProfileSwitcher() {
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [isSuper, setIsSuper] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/api/profiles`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!mounted || !d) return;
        setProfiles(d.profiles || []);
        setActive(d.active);
        setIsSuper(!!d.is_super_admin);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const switchTo = async (slug) => {
    if (slug === active || loading) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/profiles/switch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      if (r.ok) {
        // Hard reload so all components refetch under the new profile cookie
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!profiles.length) return null;
  const current = profiles.find(p => p.slug === active) || profiles[0];

  return (
    <div ref={ref} className="relative px-3 pt-3 pb-2" data-testid="profile-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="profile-switcher-btn"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-white border border-zinc-200 hover:border-zinc-400 rounded-sm transition-colors group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: current.color || '#0F172A' }}
          />
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 leading-none">Profiel</span>
          <span className="text-sm font-semibold text-zinc-900 truncate">{current.name}</span>
        </div>
        <CaretDown
          size={12}
          className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg z-50 overflow-hidden" data-testid="profile-switcher-menu">
          {profiles.map(p => (
            <button
              key={p.slug}
              type="button"
              onClick={() => switchTo(p.slug)}
              data-testid={`profile-option-${p.slug}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 transition-colors text-left ${
                p.slug === active ? 'bg-zinc-50' : ''
              }`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: p.color || '#0F172A' }}
              />
              <span className="flex-1 truncate">{p.name}</span>
              {p.slug === active && <Check size={12} weight="bold" className="text-emerald-600" />}
            </button>
          ))}
          {isSuper && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/settings/profiles'); }}
              data-testid="profile-manage-btn"
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 transition-colors text-left border-t border-zinc-100"
            >
              <Plus size={11} />
              <span>Beheer profielen</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
